/**
 * 栏目导航条 · 吸顶版
 *
 * 设计：
 *  - 初始位置在 HeroBanner 下方，作为正文起点
 *  - 滚动时 position: sticky，top:0 吸附在视口顶部
 *  - IntersectionObserver 追踪当前可见 section，激活对应导航项
 *  - 紧凑布局（移动端不能挡太多内容）：高度 ~48px
 */
import { useEffect, useState } from 'react'
import { track } from '@/lib/track'

const NAV_ITEMS = [
  { num: '01', label: '志愿填报' },
  { num: '02', label: '历年录取' },
  { num: '03', label: '考前注意' },
  { num: '04', label: '高中预警' },
  { num: '05', label: '暑期预习' }
]

export function StickyNav() {
  const [activeNum, setActiveNum] = useState<string>('01')

  useEffect(() => {
    const sections = NAV_ITEMS.map(({ num }) =>
      document.getElementById(`section-${num}`)
    ).filter(Boolean) as HTMLElement[]

    if (sections.length === 0) return

    const observer = new IntersectionObserver(
      (entries) => {
        // 找最靠近视口顶部、且正在相交的 section
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible.length > 0) {
          const id = visible[0].target.id // e.g. "section-02"
          const num = id.replace('section-', '')
          setActiveNum(num)
        }
      },
      {
        // rootMargin 让 section 在距顶部 40% 时触发
        rootMargin: '-10% 0px -50% 0px',
        threshold: 0
      }
    )

    sections.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [])

  return (
    <nav
      className="
        sticky top-0 z-30
        bg-white/95 backdrop-blur
        border-b border-ink-200
        shadow-sm
      "
    >
      <div className="container-mobile py-2">
        <div className="grid grid-cols-5 gap-1.5">
          {NAV_ITEMS.map((c) => {
            const isActive = activeNum === c.num
            return (
              <a
                key={c.num}
                href={`#section-${c.num}`}
                onClick={() => {
                  setActiveNum(c.num)
                  track('drill_click', {
                    target: 'jump_section',
                    from: 'sticky_nav',
                    anchor: `section-${c.num}`,
                    label: c.label
                  })
                }}
                className={
                  'flex flex-col items-center justify-center py-1.5 rounded-lg transition ' +
                  (isActive
                    ? 'bg-brand-50'
                    : 'active:bg-brand-50')
                }
              >
                <span
                  className={
                    'font-serif text-[13px] leading-none mb-0.5 ' +
                    (isActive ? 'text-brand-700' : 'text-accent-700')
                  }
                >
                  {c.num}
                </span>
                <span
                  className={
                    'text-[11px] leading-tight font-medium ' +
                    (isActive ? 'text-brand-700' : 'text-brand-900')
                  }
                >
                  {c.label}
                </span>
                {isActive && (
                  <span className="mt-0.5 w-3 h-0.5 rounded-full bg-brand-600" />
                )}
              </a>
            )
          })}
        </div>
      </div>
    </nav>
  )
}
